import React, { useEffect, createContext, useMemo, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { PagingContext } from '../../../contexts/paging-context';
import { getProducts } from '../../../store/products/products.api';
import { createLookupHash } from '../../../services/hash-service';
import { entitiesLoad } from '../../../store/entities/entities.actions';
import { useQuickSearchTerm } from '../../../hooks/routing/use-quick-search-term';
import { UserSettings } from '../../../enums/user-settings/user-settings';
import { useUserSetting } from '../../../hooks/use-user-setting';
import { useShouldDisplayAutoRoutingControls } from '../../../hooks/auto-routing/use-should-display-auto-routing-controls';
import { useIsAutoSplitEnabledForSeller } from '../../../hooks/use-is-auto-splitting-enabled-for-seller';
import { useEnableBundledProducts } from '../../../feature-flags/hooks/use-enable-bundled-products';
import { useEnableCommittedInventoryCounts } from '../../../feature-flags/hooks/use-enable-committed-inventory-counts';
import { useEnableInboundInventory } from '../../../feature-flags/hooks/use-enable-inbound-inventory';
import { useInventoryReorderingFeatureGate } from '../../../feature-gates/use-inventory-reordering-feature-gate';
import {
  baseColumnsDefaultConfigs as tempBaseColDefault,
  ActiveShippingLocationsColumnId,
  defaultColumnConfig,
} from '../../../store/products/grid/default-column-configuration';

import { productGridReloadSubject } from '../../../store/products/grid/product-grid.observables';
import { processBulkLoadResponse } from '../../../store/products/product-bulk-load-processor';

const ProductContext = createContext();

const useProductContext = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('ProductContext must be used within ProductContextProvider');
  }
  return context;
};

const ProductContextProvider = ({ children = null, shouldUseAggregateInventory }) => {
  const dispatch = useDispatch();
  const { page, pageSize } = useContext(PagingContext);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [productIds, setProductIds] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const searchTerm = useQuickSearchTerm() || '';
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [aggregateLastUpdated, setAggregateLastUpdated] = useState('');
  const shouldDisplayAutoRoutingControls = useShouldDisplayAutoRoutingControls();
  const isSmartRoutingEnabled = useShouldDisplayAutoRoutingControls();
  const useAutoSplitEnabled = useIsAutoSplitEnabledForSeller();
  const areBundlesEnabled = useEnableBundledProducts();
  const isCommittedInventoryCountsEnabled = useEnableCommittedInventoryCounts();
  const isInboundInventoryFlagEnabled = useEnableInboundInventory();
  const { isFeatureEnabled: isInventoryReorderingEnabled } = useInventoryReorderingFeatureGate();
  const isInboundInventoryEnabled = isInboundInventoryFlagEnabled && isInventoryReorderingEnabled;

  const applyColumnConfigurationFilters = useCallback(
    (column) => {
      const filteredColumnMap = {
        AutoSplit: useAutoSplitEnabled,
        AggregrateInventoryCounts: shouldUseAggregateInventory,
        Bundle: areBundlesEnabled,
        Committed: isCommittedInventoryCountsEnabled,
        Inbound: isInboundInventoryEnabled,
        [ActiveShippingLocationsColumnId]: shouldDisplayAutoRoutingControls,
      };

      if (filteredColumnMap[column.id] !== undefined) {
        return filteredColumnMap[column.id];
      }

      return true;
    },
    [
      shouldDisplayAutoRoutingControls,
      shouldUseAggregateInventory,
      useAutoSplitEnabled,
      areBundlesEnabled,
      isCommittedInventoryCountsEnabled,
      isInboundInventoryEnabled,
    ],
  );

  const userSetColumnConfiguration = useUserSetting(UserSettings.productGridDefaultConfig.settingName);
  const columnConfiguration = useMemo(() => {
    const tempUserColumnConfiguration = JSON.parse(userSetColumnConfiguration || '[]');
    const parsedUserColumnConfiguration = tempUserColumnConfiguration.filter(applyColumnConfigurationFilters);
    const baseColumnsDefaultConfigs = tempBaseColDefault.filter(applyColumnConfigurationFilters);
    return parsedUserColumnConfiguration.length
      ? parsedUserColumnConfiguration.map((savedConfig) => {
          const fullConfig = baseColumnsDefaultConfigs.find((c) => c.id === savedConfig.id);
          return { ...fullConfig, ...savedConfig };
        })
      : defaultColumnConfig.filter(applyColumnConfigurationFilters);
  }, [userSetColumnConfiguration, applyColumnConfigurationFilters]);

  const { sorted: initDirection, id: initOrderBy } = columnConfiguration.find(({ sorted }) => sorted) || {};
  const [orderBy, setOrderBy] = useState(initOrderBy || 'createDate');
  const [direction, setDirection] = useState(initDirection || 'descending');

  const refreshGridData = useCallback(() => {
    setEntitiesLoading(true);
    const productEntitiesPromise = getProducts({
      pageNumber: page,
      pageSize,
      orderBy,
      direction,
      searchTerm,
      appliedFilters,
    });
    productEntitiesPromise.promise
      .then(({ products, inventory, inboundQuantities, ...pageData }) => {
        // Product Entities in Redux goes first because this needs to be loaded in the store before
        // paging data is set.
        const baseEntities = processBulkLoadResponse({ products, inventory });
        const entities = baseEntities.map((entity) => ({
          ...entity,
          inboundQuantity: inboundQuantities?.[entity.productId] ?? null,
        }));
        dispatch(
          entitiesLoad({
            entities: {
              products: createLookupHash(entities || [], 'productId'),
            },
          }),
        );
        let earliestTimestamp = new Date();
        products?.forEach((product) => {
          const productDate = new Date(product.aggregateInventoryCounts?.lastUpdated);
          if (productDate < earliestTimestamp) {
            earliestTimestamp = productDate;
          }
        });

        setProductIds(pageData.currentPageProductIds);
        setTotalRecords(pageData.totalCount ?? 0);
        setAggregateLastUpdated(earliestTimestamp);
        setEntitiesLoading(false);
      })
      .catch(() => {
        setProductIds([]);
        setTotalRecords(0);
        setEntitiesLoading(false);
      });

    return productEntitiesPromise;
  }, [page, pageSize, orderBy, direction, dispatch, searchTerm, appliedFilters]);

  useEffect(() => {
    const subscription = productGridReloadSubject.subscribe(refreshGridData);

    return () => subscription.unsubscribe();
  }, [refreshGridData]);

  useEffect(() => {
    const productEntitiesPromise = refreshGridData();
    return () => productEntitiesPromise?.cancel();
  }, [refreshGridData]);

  const setColumnSort = useCallback((columnId, sortDirection) => {
    setOrderBy(columnId);
    setDirection(sortDirection);
  }, []);

  const onFiltersChanged = useCallback((filtersToApply = []) => {
    setAppliedFilters(filtersToApply);
  }, []);

  const clearFilters = useCallback(() => {
    onFiltersChanged([]);
  }, [onFiltersChanged]);

  const value = useMemo(
    () => ({
      entitiesLoading,
      productIds,
      totalRecords,
      appliedFilters,
      clearFilters,
      onFiltersChanged,
      setColumnSort,
      selectedProductIds,
      setSelectedProductIds,
      refreshGridData,
      columnConfiguration,
      shouldUseAggregateInventory,
      aggregateLastUpdated,
      isSmartRoutingEnabled,
    }),
    [
      entitiesLoading,
      productIds,
      totalRecords,
      appliedFilters,
      clearFilters,
      onFiltersChanged,
      setColumnSort,
      selectedProductIds,
      setSelectedProductIds,
      refreshGridData,
      columnConfiguration,
      shouldUseAggregateInventory,
      aggregateLastUpdated,
      isSmartRoutingEnabled,
    ],
  );

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

ProductContextProvider.propTypes = {
  children: PropTypes.node,
  shouldUseAggregateInventory: PropTypes.bool.isRequired,
};

export { useProductContext };

export default ProductContextProvider;
